import { NewScheduler } from 'apps/webcomponents/src/types/common/maintenanceWindowPatchManagementTypes';
import { RecurrenceOptions } from 'apps/webcomponents/src/types/common/schedulerTypes';
import { padHoursAndMinutes } from '../../padHoursAndMinutes.utils';
import { SchedulerHelper } from '../SchedulerHelper';

export class NewSchedulerHelper extends SchedulerHelper {
  getShortDescription(data: NewScheduler): string {
    const parts: string[] = [];

    switch (data.recurrence) {
      case RecurrenceOptions.IMMEDIATELY:
        parts.push(this.dependencies.translateFn('Immediately'));
        break;

      case RecurrenceOptions.WEEKLY:
        parts.push(this.getWeeklyRecurrence(data));
        parts.push(this.getStartingWithMonthAndYear(data));
        parts.push(this.getWeeklyFrequency(data));
        parts.push(this.getBetweenTimes(data));

        break;
      case RecurrenceOptions.MONTHLY:
        parts.push(this.getMonthlyRecurrence(data));
        parts.push(this.getStartingWithMonthAndYear(data));
        parts.push(this.getMonthlyFrequency(data));
        parts.push(this.getBetweenTimes(data));

        break;
      default:
        break;
    }

    return parts.filter(string => string.length).join(', ');
  }

  getWeeklyRecurrence(data: NewScheduler): string {
    if (data.weeklyRecurrence?.every === 1) {
      return this.dependencies.translateFn('Weekly');
    }

    return this.dependencies.translateFn('Every {weekCount} weeks', {
      weekCount: data.weeklyRecurrence?.every,
    });
  }

  getStartingWithMonthAndYear(data: NewScheduler): string {
    if (data.startDate) {
      const startDate = new Date(data.startDate * 1000);

      return this.dependencies.translateFn('starting with {startMonth}, {startYear}', {
        startMonth: this.getMonthName(startDate.getMonth()),
        startYear: startDate.getFullYear(),
      });
    }

    return '';
  }

  getWeeklyFrequency(data: NewScheduler): string {
    if (data.weeklyRecurrence?.days?.length) {
      return this.dependencies.translateFn('on {daysOfWeek}', {
        daysOfWeek: data.weeklyRecurrence.days.map(dayNumber => this.getShortDayName(dayNumber)).join(', '),
      });
    }

    return '';
  }

  getBetweenTimes(data: NewScheduler): string {
    if (
      data.between?.hasOwnProperty('startHour') &&
      data.between?.hasOwnProperty('startMinute') &&
      data.between?.hasOwnProperty('endHour') &&
      data.between?.hasOwnProperty('endMinute')
    ) {
      return this.dependencies.translateFn('between {startTime} and {endTime}', {
        startTime: padHoursAndMinutes`${data.between.startHour}:${data.between.startMinute}`,
        endTime: padHoursAndMinutes`${data.between.endHour}:${data.between.endMinute}`,
      });
    }

    return '';
  }

  getMonthlyRecurrence(data: NewScheduler): string {
    if (data.monthlyRecurrence?.every === 1) {
      return this.dependencies.translateFn('Monthly');
    }

    return this.dependencies.translateFn('Every {monthCount} months', {
      monthCount: data.monthlyRecurrence?.every,
    });
  }

  getMonthlyFrequency(data: NewScheduler): string {
    if (data.monthlyRecurrence?.frequency === 0) {
      return this.dependencies.translateFn('on day {dayNumber}', {
        dayNumber: data.monthlyRecurrence?.frequencyDayNumber,
      });
    }

    return this.dependencies.translateFn('on {frequency} {dayOfWeek}', {
      frequency: this.getMonthlyFrequencyName(data.monthlyRecurrence?.frequency),
      dayOfWeek: this.getShortDayName(data.monthlyRecurrence?.frequencyDayOfWeek),
    });
  }

  getMonthlyFrequencyName(frequencyType: number): string {
    switch (frequencyType) {
      case 1:
        return this.dependencies.translateFn('the first');
      case 2:
        return this.dependencies.translateFn('the second');
      case 3:
        return this.dependencies.translateFn('the third');
      case 4:
        return this.dependencies.translateFn('the fourth');
      case 5:
        return this.dependencies.translateFn('the last');
      default:
        return '';
    }
  }
}
